import React, { useState, useEffect, useRef } from "react";
import { usePurchaseItems } from "../context/PurchaseItemsContext";
import useDebouncedEffect from "../hooks/useDebouncedEffect";
import { apiRequest } from "../utils/api";
import { useAlert } from '../components/AlertModal';
import "../styles/PurchaseItemsList.css"; 
import { useNavigate } from 'react-router-dom';

const PurchaseItemsList = ({ purchaseOrderId, isOrderLocked, selectedStore, setSupportId, setSearchTerm, setSearchResults}) => {
  
  const [barcode, setBarcode] = useState("");
  const [conditions, setConditions] = useState([]); // 買取状態リスト
  const [accessories, setAccessories] = useState([]); // 付属品リスト
  const { items, setItems, fetchItems } = usePurchaseItems();
  const { showAlert } = useAlert();
  const [draftPrices, setDraftPrices] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();// ページ移動

  const scrollRef = useRef(null);
  const scrollStorageKey = `purchaseItemsScroll_${purchaseOrderId}`;

  // ★ 商品追加後に最下部へ移動するためのフラグ
  const shouldScrollToBottomRef = useRef(false);

  // purchaseOrderIdを監視
  useEffect(() => {
    if (purchaseOrderId) {
      fetchItems();
      fetchConditions();
      fetchAccessories();
    }
  }, [purchaseOrderId, fetchItems]);
  
  // スクロール位置復元 ＋ 商品追加時は最下部へ
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !purchaseOrderId) return;

    requestAnimationFrame(() => {
      // ★ バーコード等で商品追加した直後
      if (shouldScrollToBottomRef.current) {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: "smooth",
        });

        shouldScrollToBottomRef.current = false;

        // 最下部位置を保存
        sessionStorage.setItem(
          scrollStorageKey,
          String(el.scrollHeight)
        );

        return;
      }

      // 通常時は今まで通りスクロール位置を復元
      const saved = sessionStorage.getItem(scrollStorageKey);

      if (saved !== null) {
        el.scrollTop = Number(saved);
      }
    });
  }, [purchaseOrderId, items.length, scrollStorageKey]);

  // スクロール位置保存
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !purchaseOrderId) return;

    const handleScroll = () => {
      sessionStorage.setItem(scrollStorageKey, String(el.scrollTop));
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [purchaseOrderId, scrollStorageKey]);

  const fetchConditions = async () => {
    try {
      const data = await apiRequest("inventory/purchase_conditions/");
      setConditions(data);
    } catch (error) {
      console.error("Error fetching conditions:", error);
    }
  };

  const fetchAccessories = async () => {
    try {
      const data = await apiRequest("inventory/purchase_accessories/");
      setAccessories(data);
    } catch (error) {
      console.error("Error fetching accessories:", error);
    }
  };

  const handleQuantityChange = async (id, value) => {
    const updatedItems = items.map((item) =>
      item.id === id ? { ...item, quantity: value, subtotal: item.unit_price * value } : item
    );
    setItems(updatedItems);
    try {
      await apiRequest(`inventory/purchase_items/${id}/`, "PUT", {
        quantity: value,
        subtotal: updatedItems.find(i => i.id === id).subtotal
      });

      fetchItems();// 最新データ取得
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const onPriceInputChange = (itemId, value) => {
    const price = parseInt(value, 10);
    const quantity = items.find((item) => item.id === itemId)?.quantity || 1;

    setDraftPrices((prev) => ({ ...prev, [itemId]: price }));

    // UIにはすぐ反映（視覚的に即レスポンス）
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, unit_price: price, subtotal: price * quantity } : item
      )
    );
  };

  // デバウンス送信
  useDebouncedEffect(() => {
    Object.entries(draftPrices).forEach(async ([itemIdStr, price]) => {
      const itemId = parseInt(itemIdStr, 10);
      const quantity = items.find((item) => item.id === itemId)?.quantity || 1;

      try {
        await apiRequest(`inventory/purchase_items/${itemId}/`, "PUT", {
          unit_price: price,
          subtotal: price * quantity,
        });
        fetchItems(); // 最新状態取得
      } catch (error) {
        console.error(`Error updating price for item ${itemId}:`, error);
      }
    });

    // リクエスト後に draft をクリア（オプション）
    setDraftPrices({});
  }, [draftPrices], 500); // ← 500ms 入力停止後に送信

  const handleConditionChange = async (itemId, selectedConditionId) => {
    const selectedCondition = conditions.find((condition) => condition.id === parseInt(selectedConditionId, 10));

    if (selectedCondition?.id === 7 || selectedCondition?.id === 8) {
      onPriceInputChange(itemId, 0);
    }

    // フロントエンドの状態を更新（即座にUI反映）
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, condition: selectedCondition } : item
      )
    );

    try {
        // `condition_id` を送信する
        const payload = { condition_id: parseInt(selectedConditionId, 10) };

        await apiRequest(`inventory/purchase_items/${itemId}/`, "PUT", payload);

        // 最新データ取得（API反映後のデータを取得）
        fetchItems();
    } catch (error) {
        console.error("Error updating condition:", error);
    }
  };

  const handleBoxConditionChange = async (id, value) => {
    try {
      await apiRequest(`inventory/purchase_items/${id}/`, "PUT", { box_condition: value });
      fetchItems();// 最新データ取得
    } catch (error) {
      console.error("Error updating box condition:", error);
    }
  };

  const handleAccessoryChange = async (itemId, selectedAccessoryId) => {
    const selectedAccessory = accessories.find((accessory) => accessory.id === parseInt(selectedAccessoryId, 10));
    

    // フロントエンドの状態を更新（即座にUI反映）
    setItems((prevItems) =>
        prevItems.map((item) =>
            item.id === itemId ? { ...item, accessories: selectedAccessory } : item
        )
    );

    try {
        // `accessories_id` を送信（数値）
        const payload = { accessories_id: parseInt(selectedAccessoryId, 10) };

        await apiRequest(`inventory/purchase_items/${itemId}/`, "PUT", payload);

        // 最新データ取得（API反映後のデータを取得）
        fetchItems();
    } catch (error) {
        console.error("Error updating accessories:", error);
    }
  };
  

  const handleDelete = async (id) => {
    try {
        await apiRequest(`inventory/purchase_items/${id}/`, "DELETE");

        // フロント側でも即時削除
        setItems((prevItems) => prevItems.filter((item) => item.id !== id));


        // 最新データ取得
        fetchItems();
    } catch (error) {
        console.error("Error deleting item:", error);
    }
  };
  
  // バーコードの入力を受けて状態を更新
  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
  };
  // エンターキーが押されたら商品追加処理を呼び出す
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddItem();
    }
  };

  const handleAddItem = async () => {
    if (isOrderLocked) {
      showAlert("伝票が確定されていません。先に伝票を確定してください。");
      return;
    }

    if (!purchaseOrderId) {
      showAlert("伝票IDがありません。伝票を保存してから追加してください。");
      return;
    }

    if (!barcode.trim()) {
      showAlert("バーコードを入力してください");
      return;
    }

    try {
      // 正しいエンドポイントを使用
      const inventoryResponse = await apiRequest(`inventory/jan/${barcode}/`, "GET");

      if (!inventoryResponse || inventoryResponse.length === 0) {
        showAlert("該当する商品が見つかりませんでした");
        return;
      }
      // 配列か単一オブジェクトかを判定
      const inventoryItem = Array.isArray(inventoryResponse) ? inventoryResponse[0] : inventoryResponse;

      // `inventoryItem` のバリデーションチェック
      if (!inventoryItem || typeof inventoryItem !== "object" || !inventoryItem.id) {
        showAlert("商品データが不正です。管理者に連絡してください。");
        console.error("Invalid inventory item:", inventoryItem);
        return;
      }
      // store_inventory から価格を取得(本店指定)
      const price = inventoryItem.buy_specialprice_switch[1]
        ? inventoryItem.buy_specialprice[1]
        : inventoryItem.buy_price[1];
      
      const newItem = {
        purchase_order_id: Number(purchaseOrderId), // 伝票IDを明示 Numberで確実に数字のみにする
        item_id: inventoryItem.id, // 商品マスターID
        quantity: 1,
        unit_price: price || 0,  // `price` を設定
        subtotal: price || 0,     // `price` を設定
        condition_id: 1, // 買取状態のID (初期値は 1)
        box_condition: "",
        accessories_id: null // 付属品のID (初期値は null)
      };

      await apiRequest(`inventory/purchase_orders/${purchaseOrderId}/items/`, "POST", newItem);
      
      // ★ 次のitems更新時に最下部へスクロール
      shouldScrollToBottomRef.current = true;
      
      fetchItems();// 最新データ取得
      setBarcode("");
    } catch (error) {
      console.error("Error adding item:", error);
      
      
      showAlert("商品追加に失敗しました。");
    }
  };
  const SupporSearch = (id) => {
    setSearchTerm("");
    setSearchResults([]); // 検索結果もリセット
    setSupportId(id)
  };
  
  // ソート処理関数
  const SORT_KEYS = {
    'item_id': (item) => Number(item.item?.id) || 0,
    'item_name': (item) => item.item?.item_name || '',
    'unit_price': (item) => Number(item.unit_price) || 0,
  };
  const sortedItems = [...items].sort((a, b) => {
    if (!sortConfig.key) return 0;
  
    const getValue = SORT_KEYS[sortConfig.key];
    const aVal = getValue(a);
    const bVal = getValue(b);
  
    if (aVal == null) return 1;
    if (bVal == null) return -1;
  
    if (typeof aVal === 'string') {
      return sortConfig.direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
  
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });
  
  // ソートclick関数
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div className="purchase-items-container">
      <h2 className="section-title">買取明細</h2>
      <div className="search-bar">
        <input
          type="text"
          placeholder="バーコードを入力"
          className="barcode-input"

          value={barcode}
          onChange={handleBarcodeChange}
          onKeyDown={handleKeyDown} 
        />
      </div>
      <div className="purchase-table-container" ref={scrollRef}>
      <table className="table">
        <thead>
        <tr>
          <th className="img-col" onClick={() => handleSort('item_id')}>画像▲▼</th>
          <th className="item_name-col" onClick={() => handleSort('item_name')}>商品名▲▼</th>
          <th>バーコード</th>
          <th className="manufacturer-col">ブランド</th>
          <th className="quantity-col">数量</th>
          <th className="price-col" onClick={() => handleSort('unit_price')}>価格▲▼</th>
          <th className="subtotal-col">小計</th>
          <th>状態</th>
          <th>箱</th>
          <th>付属品</th>
          <th>💀</th>
        </tr>
        </thead>
        <tbody>
          {sortedItems.map((item) => (
            <tr key={item.id}>
              <td>
                <div className="purchase-table-imgbox">
                <img
                  src={`/media/product_images/${item.item?.sku || "default"}.jpg`}
                  className="product-image"
                  alt="商品画像"
                />
                </div>
              </td>
              <td><div className="navigatedetail" onClick={() => navigate(`/Inventory_detail/${item.item?.id}/${selectedStore}`)}>{item.item?.item_name || "N/A"}</div></td>
              <td className="jan-col">{item.item?.jan || "N/A"}</td>
              <td>{item.item?.manufacturer || "N/A"}</td>
              <td>
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value, 10))}
                  className="input-quantity"
                  onWheel={(e) => e.target.blur()}
                />
              </td>
              <td className="input-price-area">
                <input
                  type="number"
                  value={
                    item.condition?.id === 7 || item.condition?.id === 8
                      ? 0
                      : parseInt(item.unit_price, 10)
                  }
                  onChange={(e) => {
                    if (item.condition?.id === 7 || item.condition?.id === 8) {
                      onPriceInputChange(item.id, 0);
                    } else {
                      onPriceInputChange(item.id, parseInt(e.target.value, 10));
                    }
                  }}
                  className="input-price"
                  onClick={() => SupporSearch(item.item.id)}
                  onWheel={(e) => e.target.blur()}
                />円
              </td>
              <td className="subtotal-value">{parseInt(item.unit_price, 10) * item.quantity} 円</td>
              <td>
                <select 
                  value={item.condition?.id || ""} 
                  onChange={(e) => handleConditionChange(item.id, e.target.value)}
                  style={{
                    color:
                      item.condition?.id === 1
                        ? "#ffffff"
                        : item.condition?.id === 8
                        ? "#ff0000"
                        : "#ffff00",
                  }}
                >
                  <option value="">選択</option>
                  {conditions.map((condition) => (
                    <option key={condition.id} value={condition.id}>
                      {condition.name}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={item.box_condition || ""}
                  onChange={(e) => handleBoxConditionChange(item.id, e.target.value)}
                  style={{
                    color:
                      item.box_condition === "箱傷み" ||
                      item.box_condition === "箱破損"
                        ? "#ffff00"
                        : "#ffffff",
                  }}
                >
                  <option value="">選択</option>
                  <option value="箱傷み">箱傷み</option>
                  <option value="箱破損">箱破損</option>
                </select>
              </td>
              <td>
                <select value={item.accessories?.id || ""} onChange={(e) => handleAccessoryChange(item.id, e.target.value)}>
                  <option value="">選択</option>
                  {accessories.map((accessory) => (
                    <option key={accessory.id} value={accessory.id}>
                      {accessory.name}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button className="delete-btn" onClick={() => handleDelete(item.id)}>✖</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default PurchaseItemsList;

